/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TrmUserAgencyProps {
  id: string;
  trmUserId: string;
  agencyId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TrmUserAgencyRecord extends Record<TrmUserAgencyProps>({
  id: null as any,
  trmUserId: null as any,
  agencyId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    trmUserId: string;
    agencyId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TrmUserAgencyRecord {
    if (json == null) {
      throw new Error('TrmUserAgencyRecord.fromJS: parameter cannot be null');
    }
    return new TrmUserAgencyRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TrmUserAgencyJson.id',
      ),
      trmUserId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'trmUserId', '00000000-0000-0000-0000-000000000000'),
        'TrmUserAgencyJson.trmUserId',
      ),
      agencyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'agencyId', '00000000-0000-0000-0000-000000000000'),
        'TrmUserAgencyJson.agencyId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'TrmUserAgencyJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    trmUserId: string;
    agencyId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TrmUserAgencyRecord, Error[]] {
    if (json == null) {
      throw new Error('TrmUserAgencyRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TrmUserAgencyRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TrmUserAgencyJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      trmUserId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'trmUserId', '00000000-0000-0000-0000-000000000000'),
            'TrmUserAgencyJson.trmUserId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      agencyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'agencyId', '00000000-0000-0000-0000-000000000000'),
            'TrmUserAgencyJson.agencyId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'TrmUserAgencyJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TrmUserAgencyProps) {
    super(props);
    if (props == null) {
      throw new Error('TrmUserAgencyRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TrmUserAgencyViewModelBase implements TrmUserAgencyProps {
  protected readonly _props: TrmUserAgencyProps;
  private _json?: TrmUserAgencyProps;

  get id(): string {
    return this._props.id;
  }
  get trmUserId(): string {
    return this._props.trmUserId;
  }
  get agencyId(): string {
    return this._props.agencyId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: TrmUserAgencyProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        trmUserId: this._props.trmUserId,
        agencyId: this._props.agencyId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTrmUserAgencyEntities = (state: AppState) =>
  state.entityStores.trmUserAgency.entities;

export const selectTrmUserAgencyById = (id: string) => (state: AppState) =>
  state.entityStores.trmUserAgency.entities.get(id);

export const selectTrmUserAgencyMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.trmUserAgency.meta.get(id);

export const selectTrmUserAgencyAndMetadataById = (id: string) =>
  createSelector(
    selectTrmUserAgencyById(id),
    selectTrmUserAgencyMetadataById(id),
    (trmUserAgency, meta) => ({ trmUserAgency, meta }),
  );

export function selectTrmUserAgencyEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTrmUserAgencyEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TrmUserAgencyRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTrmUserAgencyEntitiesByTrmUserId(trmUserId: string) {
  return createSelector(selectAllTrmUserAgencyEntities, (entities) =>
    entities.filter((e) => e.trmUserId === trmUserId).valueSeq(),
  );
}

export function selectTrmUserAgencyEntitiesByAgencyId(agencyId: string) {
  return createSelector(selectAllTrmUserAgencyEntities, (entities) =>
    entities.filter((e) => e.agencyId === agencyId).valueSeq(),
  );
}

export function selectTrmUserAgencyEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllTrmUserAgencyEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
